import Vue from 'vue';
import { Store } from 'vuex';

import PlayboardWorker from 'worker-loader!./playboard.worker';

import { CONTAINER, TYPES } from '../config';

import { ApiEnum, DeviceEnum, EnvironmentEnum, LocaleEnum } from '../enums';
import { WorkerMessageTypeEnum } from '../enums/worker-message-type.enum';

import { capitalizeSentence } from '../filters';

import { ApiError, AuthTokenInfo, Dictionary } from '../models';

import { TaggingApiService, TaggingService } from '../services';

import { AUTH_CONSTS } from '../store/auth/auth.consts';
import { CONTEXT_CONSTS } from '../store/context/context.consts';
import { MEDIA_CONSTS } from '../store/media/media.consts';
import { MESSAGES_CONSTS } from '../store/messages/messages.consts';

import { ErrorsUtils } from './errors.utils';
import { compileMessage } from './messages.utils';
import { NumberUtils } from './number.utils';

declare module 'vue/types/vue' {
  interface Vue {
    $epb: {
      device: DeviceEnum;
      isHandheldDevice: boolean;
      locale: LocaleEnum;
      messages: Dictionary;
      utils: {
        formatNumber: (numberValue: number, options?, roundLongNumber?: boolean, roundBillions?: boolean) => string;
        compileMessage: (message: string, params: Dictionary<string>) => string;
        decodeErrorResponse: (errorResponse: any, api: ApiEnum) => ApiError;
        decodeErrorMessage: (errorResponse: any, api: ApiEnum) => { title: string; message: string; };
      }
    };
  }
}

export function initApp(config: any = {}): void {
  Vue.config.productionTip = false;

  const store = CONTAINER.get<Store<any>>(TYPES.Store);

  store.dispatch(`${ CONTEXT_CONSTS.moduleName }/${ CONTEXT_CONSTS.actions.setContext }`, {
    environment: ((config.environment as EnvironmentEnum) || EnvironmentEnum.LIVE)
  });

  const taggingUrl = (config.environment === EnvironmentEnum.STAGING)
    ? 'https://api.staging.empathy.co/tagging-playboard/v1'
    : 'https://api.empathy.co/tagging-playboard/v1';
  CONTAINER.rebind<TaggingService>(TYPES.TaggingService).toConstantValue(new TaggingApiService(taggingUrl));

  Vue.prototype.$epb = {
    get device() {
      return store.getters[`${ MEDIA_CONSTS.moduleName }/${ MEDIA_CONSTS.getters.device }`];
    },
    get isHandheldDevice() {
      return (store.getters[`${ MEDIA_CONSTS.moduleName }/${ MEDIA_CONSTS.getters.device }`] < DeviceEnum.SMALL_DESKTOP);
    },
    get locale() {
      return store.getters[`${ MESSAGES_CONSTS.moduleName }/${ MESSAGES_CONSTS.getters.locale }`];
    },
    get messages() {
      return store.getters[`${ MESSAGES_CONSTS.moduleName }/${ MESSAGES_CONSTS.getters.messages }`];
    },
    utils: {
      formatNumber: (numberValue: number, options = {}, roundLongNumber = false, roundBillions = true) => {
        const locale = store.getters[`${ MESSAGES_CONSTS.moduleName }/${ MESSAGES_CONSTS.getters.locale }`];
        return NumberUtils.format(numberValue, locale, options, roundLongNumber, roundBillions);
      },
      compileMessage,
      decodeErrorResponse: ErrorsUtils.decodeErrorResponse,
      decodeErrorMessage: (errorResponse: any, api: ApiEnum): { title: string; message: string; } => {
        const errorsMessages = store.getters[`${ MESSAGES_CONSTS.moduleName }/${ MESSAGES_CONSTS.getters.messages }`].errors;
        return ErrorsUtils.decodeErrorMessage(errorResponse, api, errorsMessages);
      }
    }
  };

  Vue.filter('capitalizeSentence', capitalizeSentence);

  window.addEventListener('resize', function () {
    store.dispatch(`${ MEDIA_CONSTS.moduleName }/${ MEDIA_CONSTS.actions.setViewportWidth }`, window.innerWidth);
  });
}

export function initWorker(): void {
  const playboardWorker = new PlayboardWorker();

  const store = CONTAINER.get<Store<any>>(TYPES.Store);

  const postRefreshAuthTokenMessage = () => {
    const keepLoggedIn: boolean = store.getters[`${ AUTH_CONSTS.moduleName }/${ AUTH_CONSTS.getters.keepLoggedIn }`];
    const authTokenInfo: AuthTokenInfo = store.getters[`${ AUTH_CONSTS.moduleName }/${ AUTH_CONSTS.getters.tokenInfo }`];
    playboardWorker.postMessage({
      type: WorkerMessageTypeEnum.REFRESH_AUTH_TOKEN,
      payload: (keepLoggedIn ? authTokenInfo.expirationDate : undefined)
    });
  };

  const onRefreshAuthTokenMessage = async (payload: any) => {
    try {
      await store.dispatch(`${ AUTH_CONSTS.moduleName }/${ AUTH_CONSTS.actions.refresh }`);
    } catch (error) {
      /* tslint:disable */
      console.trace(error);
    }
  };

  playboardWorker.onmessage = (message: { data: { type: WorkerMessageTypeEnum, payload: any } }) => {
    return {
      [WorkerMessageTypeEnum.REFRESH_AUTH_TOKEN]: onRefreshAuthTokenMessage
    }[message.data.type](message.data.payload);
  };

  store.subscribe((mutation) => {
    if ((mutation.type === `${ AUTH_CONSTS.moduleName }/${ AUTH_CONSTS.mutations.setState }`) ||
      (mutation.type === `${ AUTH_CONSTS.moduleName }/${ AUTH_CONSTS.mutations.clearModule }`)) {
      postRefreshAuthTokenMessage();
    }
  });
}
